import type { ExpenseInput } from "../types";
import type { ReceiptCategory, ReceiptCategoryTotal, ReceiptDraft, ReceiptSplitPlan } from "../types/receipt";
import { checkedAllocations, distributeMinor, minorAmount } from "./allocations";
import { formatMinor } from "./money-math";

export function signedMinor(value: string): number | null {
  const negative = value.startsWith("-");
  const amount = minorAmount(negative ? value.slice(1) : value);
  return amount === null ? null : negative ? -amount : amount;
}
export const receiptCategoryKey = (value: ReceiptCategory) => JSON.stringify([value.category, value.subcategory]);

/** Items already include their line discounts. Only explicitly additional adjustments are applied. */
export function receiptCategoryTotals(receipt: ReceiptDraft, expense = receipt.expense) {
  const plan = receipt.categorySplit;
  if (!plan) throw new Error("Assign receipt items to categories first.");
  const total = minorAmount(expense.amount);
  if (total === null || total <= 0) throw new Error("Set the final receipt total before splitting.");
  const groups = new Map<string, ReceiptCategoryTotal>();
  const group = (choice: ReceiptCategory) => {
    if (!choice?.category.trim()) throw new Error("Choose a category for every receipt item.");
    const key = receiptCategoryKey(choice);
    if (!groups.has(key)) groups.set(key, { ...choice, items: 0, discount: 0, tax: 0, fee: 0, other: 0, total: 0 });
    return groups.get(key)!;
  };
  const values = receipt.items.map(item => {
    const amount = minorAmount(item.total);
    if (amount === null) throw new Error(`Check the line total for ${item.name || item.original}. Missing prices cannot be treated as zero.`);
    const row = group(plan.assignments[item.id]);
    row.items += amount; row.total += amount;
    return amount;
  });
  // Receipt-wide discounts change the basis for later tax/fee allocation, never item price history.
  const discounts = plan.adjustments.filter(adjustment => adjustment.kind === "discount");
  const others = plan.adjustments.filter(adjustment => adjustment.kind !== "discount");
  for (const adjustment of [...discounts, ...others]) {
    if (adjustment.included) continue;
    const amount = signedMinor(adjustment.amount);
    if (amount === null) throw new Error(`Enter the amount for ${adjustment.label || "this receipt adjustment"}.`);
    if (adjustment.kind === "discount" && amount > 0) throw new Error("Enter discounts as negative amounts.");
    if ((adjustment.kind === "tax" || adjustment.kind === "fee") && amount < 0)
      throw new Error("Tax and fees must be zero or positive. Use Other for a refund adjustment.");
    const matches = receipt.items.map((item, index) => ({ item, index })).filter(({ item }) =>
      adjustment.target === "all" || (adjustment.target === "items" ? adjustment.itemIds.includes(item.id)
        : receiptCategoryKey(plan.assignments[item.id]) === receiptCategoryKey(adjustment)));
    if (adjustment.target === "category" && adjustment.kind !== "discount") {
      const row = group(adjustment); row[adjustment.kind] += amount; row.total += amount;
      continue;
    }
    if (!matches.length) throw new Error(`Choose the items or category for ${adjustment.label || "this adjustment"}.`);
    // A printed adjustment for one item is exact even when that item itself was free.
    const weights = matches.length === 1 ? [1] : matches.map(({ index }) => values[index]);
    const shares = distributeMinor(amount, weights);
    matches.forEach(({ item, index }, position) => {
      const share = shares[position], row = group(plan.assignments[item.id]);
      row[adjustment.kind] += share; row.total += share;
      if (adjustment.kind === "discount") {
        values[index] += share;
        if (values[index] < 0) throw new Error(`Discounts exceed the value of ${item.name || item.original}.`);
      }
    });
  }
  const rows = [...groups.values()];
  if (rows.some(row => row.total < 0)) throw new Error("A category total cannot be negative. Check the receipt adjustments.");
  return { rows, difference: total - rows.reduce((sum, row) => sum + row.total, 0) };
}

/** Shared submit boundary for ordinary receipt saves, Moo and attachment review. */
export function receiptSplitExpense(receipt: ReceiptDraft, expense = receipt.expense): ExpenseInput {
  if (receipt.categorySplit?.mode === "manual") {
    if (!expense.allocations) throw new Error("Review the manual receipt category amounts before saving.");
    return { ...expense, allocations: checkedAllocations(expense) };
  }
  if (receipt.categorySplit?.mode !== "items") return expense;
  const { rows, difference } = receiptCategoryTotals(receipt, expense);
  if (difference) throw new Error(`Receipt categories differ from the final total: items and additional adjustments ${difference < 0 ? "exceed it" : "fall short"} by ${formatMinor(Math.abs(difference))} ${receipt.currency}. Review line prices, discounts, tax and fees.`);
  const positive = rows.filter(row => row.total > 0);
  const household = expense.reportableAmount === undefined ? null : minorAmount(expense.reportableAmount);
  if (expense.reportableAmount !== undefined && (household === null || household > (minorAmount(expense.amount) ?? 0)))
    throw new Error("Check the household total before splitting this receipt.");
  const shares = household === null ? [] : distributeMinor(household, positive.map(row => row.total));
  const allocations = positive.map((row, index) => ({ category: row.category, subcategory: row.subcategory,
    amount: formatMinor(row.total), ...(household !== null ? { reportableAmount: formatMinor(shares[index]) } : {}) }));
  const result = { ...expense, category: allocations[0]?.category || expense.category,
    subcategory: allocations[0]?.subcategory || "", allocations };
  checkedAllocations(result);
  return result;
}

export function startReceiptSplit(receipt: ReceiptDraft, expense: ExpenseInput): ReceiptSplitPlan {
  return { mode: "items", adjustments: receipt.categorySplit?.adjustments || [],
    assignments: Object.fromEntries(receipt.items.map(item => [item.id, receipt.categorySplit?.assignments[item.id]
      || { category: expense.category || "Uncategorized", subcategory: expense.subcategory }])) };
}
